import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const FeatureFlagsContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState({});
  const [isBeta, setIsBeta] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      // Build the request manually so we can include the token if present
      const token = localStorage.getItem('divispend_access_token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        headers['X-Access-Token'] = token;
      }
      const { data } = await axios.get(`${API}/feature-flags/visible`, { headers });
      setFlags(data.flags || {});
      setIsBeta(!!data.is_beta);
      setIsAdmin(!!data.is_admin);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
      setFlags({});
      setIsBeta(false);
      setIsAdmin(false);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch when storage changes (login/logout in another tab)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'divispend_access_token') refresh();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [refresh]);

  const isEnabled = useCallback((key) => !!flags[key], [flags]);

  const value = useMemo(() => ({ flags, isEnabled, isBeta, isAdmin, loaded, refresh }),
    [flags, isEnabled, isBeta, isAdmin, loaded, refresh]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  return ctx;
}

// Convenience wrapper component
export function FeatureGate({ flag, children, fallback = null }) {
  const { isEnabled, loaded } = useFeatureFlags();
  if (!loaded) return null;
  return isEnabled(flag) ? <>{children}</> : fallback;
}
